import frontPackage from '../../package.json';

const MAX_STRING_LENGTH = 3000;
const DEDUPE_WINDOW_MS = 60 * 1000;
const reportedErrors = new Map();

function isEnabled() {
  const raw = process.env.VUE_APP_ERROR_REPORTING_ENABLED;
  if (raw == null || String(raw).trim() === '') {
    // En prod : actif par défaut si le back a ERROR_REPORTING_ENABLED (évite un rebuild oublié).
    return process.env.NODE_ENV === 'production';
  }
  const value = String(raw).toLowerCase();
  return value === 'true' || value === '1';
}

function truncate(value, maxLength = MAX_STRING_LENGTH) {
  const str = String(value || '');
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}... [truncated]`;
}

function getEndpoint() {
  const explicitEndpoint = String(process.env.VUE_APP_ERROR_REPORT_ENDPOINT || '').trim();
  if (explicitEndpoint) return explicitEndpoint;

  let base = String(process.env.VUE_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  const buildFromBase = (apiBase) =>
    apiBase.endsWith('/api/v1')
      ? `${apiBase}/error-reports/client`
      : `${apiBase}/api/v1/error-reports/client`;

  // Même logique que plugins/api.js : proxy relatif uniquement en dev localhost.
  if (typeof window !== 'undefined') {
    const { origin, hostname } = window.location;
    const isLocalhost = /^localhost$|^127\.0\.0\.1$/.test(hostname);
    if (isLocalhost && base.startsWith('http') && !base.startsWith(origin)) {
      return buildFromBase('/api/v1');
    }
  }

  if (!base) return buildFromBase('/api/v1');
  if (base.startsWith('/')) return buildFromBase(base);
  return buildFromBase(base);
}

function getRoute(router) {
  try {
    return router?.currentRoute?.value?.fullPath || window.location.pathname + window.location.search;
  } catch (_err) {
    return '';
  }
}

function getUserContext(store) {
  try {
    const session = store?.getters?.getSessionInfo || {};
    const issuer = store?.getters?.getCurrentIssuerInfo || {};
    return {
      userEmail:
        session?.connexionEmail ||
        session?.email ||
        issuer?.adminEmail ||
        issuer?.contact?.email ||
        null,
      issuerDid: issuer?.did || session?.issuerDid || null,
    };
  } catch (_err) {
    return { userEmail: null, issuerDid: null };
  }
}

function getReleaseVersion() {
  const frontVersion = frontPackage?.version || process.env.VUE_APP_VERSION || '';
  const backVersion =
    typeof window !== 'undefined'
      ? window.__MYDID_API_VERSION__ || ''
      : '';
  if (frontVersion && backVersion) return `front:${frontVersion} / back:${backVersion}`;
  if (frontVersion) return `front:${frontVersion}`;
  if (backVersion) return `back:${backVersion}`;
  return '';
}

function errorToPayload(error, fallback = {}) {
  const message = truncate(
    fallback.message || error?.message || error?.reason?.message || String(error || 'Unknown error'),
    1000
  );
  return {
    message,
    stack: truncate(fallback.stack || error?.stack || error?.reason?.stack || '', 6000),
    filename: fallback.filename || null,
    lineno: fallback.lineno || null,
    colno: fallback.colno || null,
    componentInfo: fallback.componentInfo || null,
    context: fallback.context || {},
  };
}

function shouldSkip(error, message = '') {
  const text = String(message || error?.message || error || '');
  if (!text) return false;
  if (error?.isAxiosError || error?.name === 'AxiosError') return true;
  if (text.includes('Network Error')) return true;
  if (text.includes('Loading chunk') || text.includes('ChunkLoadError')) return true;
  if (text.includes('Failed to fetch dynamically imported module')) return true;
  if (text.includes('Importing a module script failed')) return true;
  return false;
}

function shouldSend(payload) {
  const key = `${payload.message}|${payload.stack.split('\n').slice(0, 2).join('|')}|${payload.route}`;
  const now = Date.now();
  const previous = reportedErrors.get(key) || 0;
  if (now - previous < DEDUPE_WINDOW_MS) return false;
  reportedErrors.set(key, now);
  return true;
}

function sendReport(payload) {
  if (!isEnabled() || typeof window === 'undefined') return;
  if (!shouldSend(payload)) return;

  const endpoint = getEndpoint();
  const body = JSON.stringify(payload);

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, blob)) return;
    } catch (_err) {
      // fallback to fetch below
    }
  }

  window.fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'omit',
    keepalive: true,
    body,
  }).catch(() => {});
}

export function reportFrontendError(error, options = {}) {
  if (!isEnabled()) return;
  const payload = {
    service: 'frontend',
    severity: options.severity || 'error',
    environment:
      process.env.VUE_APP_ERROR_REPORT_ENV ||
      process.env.VUE_APP_ENV ||
      process.env.NODE_ENV ||
      'unknown',
    release: getReleaseVersion(),
    route: getRoute(options.router),
    url: typeof window !== 'undefined' ? window.location.href : '',
    locale: options.locale || null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ...getUserContext(options.store),
    ...errorToPayload(error, options),
  };
  if (shouldSkip(error, payload.message)) return;
  sendReport(payload);
}

export function installFrontendErrorReporting(app, { router, store, i18n } = {}) {
  if (!isEnabled() || !app || typeof window === 'undefined') return;

  const previousVueHandler = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    reportFrontendError(err, {
      router,
      store,
      locale: i18n?.global?.locale?.value,
      componentInfo: info || null,
      context: {
        component:
          instance?.type?.name ||
          instance?.type?.__name ||
          instance?.$options?.name ||
          null,
      },
    });
    if (typeof previousVueHandler === 'function') {
      previousVueHandler(err, instance, info);
    } else {
      console.error(err);
    }
  };
}
